import { SelectValue } from '../../../../../Components/Base/Select/Select';
import Text from '../../../../../Components/Base/Text/Text';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import FormCondition from '../../../../../Components/Form/FormCondition';
import FormContainer from '../../../../../Components/Form/FormContainer';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import DateField from '../../../../../Components/Form/components/DateField';
import InputField from '../../../../../Components/Form/components/InputField';
import NumberField from '../../../../../Components/Form/components/NumberField';
import RadioListField from '../../../../../Components/Form/components/RadioListField';
import SelectField from '../../../../../Components/Form/components/SelectField';
import useTranslation from '../../../../../Hooks/useTranslation';
import {
	FieldNames,
	HealthPlanContext,
} from '../../../../../Services/HealthBabyPlanDataContext';
import { joinClasses } from '../../../../../Utils/className.util';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import PrescriptionMedicamentsSelectField from '../../../../Prescription/Components/PrescriptionMedicamentsSelectField';
//@ts-ignore
import styles from './LabResultsForm.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import SavingSpinner from '../../../../../Components/Base/Loading/SavingSpinner';
import useFormDraft from '../../../../../Components/Form/FormDraftState/useFormDraft';
import { FormDraftStateKey } from '../../../../../Components/Form/FormDraftState/FormDraftStateKey';

const BLOOD_GROUPS = ['A', 'AB', 'B', '0'];

enum RESULT_TYPES {
	POSITIVE = 'positive',
	NEGATIVE = 'negative',
	OTHERS = 'others',
	NOT_COLLECTED = 'notCollected',
	DONE = 'done',
	NOT_DONE = 'notDone',
}

const RESULT_OPTIONS = [
	{
		title: 'base.positive',
		value: RESULT_TYPES.POSITIVE,
	},
	{
		title: 'base.negative',
		value: RESULT_TYPES.NEGATIVE,
	},
	{
		title: 'base.others',
		value: RESULT_TYPES.OTHERS,
	},
];

const RESULT_QUESTIONS = [
	{ name: 'bwr', data: RESULT_OPTIONS },
	{ name: 'antibodies', data: RESULT_OPTIONS },
	{ name: 'hiv', data: RESULT_OPTIONS },
	{ name: 'hbsag', data: RESULT_OPTIONS },
	{ name: 'hepc', data: RESULT_OPTIONS },
	{
		name: 'amcCvsNipt',
		data: [
			{ title: 'base.labResults.done', value: RESULT_TYPES.DONE },
			{ title: 'base.labResults.notDone', value: RESULT_TYPES.NOT_DONE },
		],
	},
];

const VALIDATION_TRANSLATION_PREFIX = 'validation.labResultsForm';
const APPLICATION_OF_ANTI_D_LABEL = 'applicationOfAntiD';

type ITranslations = (key: string) => string | undefined;

const getMultiFormSchema = (dateMessage?: string, valueMessage?: string) =>
	yup.array().of(
		yup
			.object({
				collectionDate: yup
					.date()
					.transform((curr, orig) => (orig === '' ? null : curr))
					.nullable()
					.required(dateMessage),
				value: yup.string().nullable().required(valueMessage),
			})
			.nullable()
			.default({}),
	);

const getValidationSchema = (t: ITranslations) =>
	yup
		.object({
			[FieldNames.LAB_RESULTS]: yup
				.object({
					bloodGroup: yup
						.object({
							factor: yup
								.object()
								.nullable()
								.test(
									'object-check',
									t(`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.factor`) ?? '',
									score => !isEmpty(score),
								),
							factorOther: yup
								.string()
								.nullable()
								.when('factor', {
									is: (factor: SelectValue) => factor?.value === 'others',
									then: yup
										.string()
										.required(
											t(
												`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.factorOther`,
											),
										),
								}),
							type: yup
								.object()
								.nullable()
								.test(
									'object-check',
									t(`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.type`) ?? '',
									score => !isEmpty(score),
								),
							[APPLICATION_OF_ANTI_D_LABEL]: yup.string().nullable(),
							antiD: yup
								.object({
									date: yup
										.date()
										.transform((curr, orig) => (orig === '' ? null : curr))
										.nullable()
										.when(APPLICATION_OF_ANTI_D_LABEL, {
											is: 'yes',
											then: yup
												.date()
												.transform((curr, orig) => (orig === '' ? null : curr))
												.nullable()
												.required(
													t(
														`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.antiD.date`,
													),
												),
										}),
									medicineName: yup
										.object({
											id: yup.string(),
											item: yup.object(),
											label: yup.string(),
										})
										.when(APPLICATION_OF_ANTI_D_LABEL, {
											is: 'yes',
											then: yup
												.object({
													id: yup
														.string()
														.nullable()
														.required(
															t(
																`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.antiD.medicineName`,
															),
														),
													item: yup.object(),
													label: yup.string(),
												})
												.nullable()
												.required(
													t(
														`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.antiD.medicineName`,
													),
												),
										})
										.nullable(),
									dose: yup.string().when(APPLICATION_OF_ANTI_D_LABEL, {
										is: 'yes',
										then: yup
											.string()
											.required(
												t(
													`${VALIDATION_TRANSLATION_PREFIX}.bloodGroup.antiD.dose`,
												),
											)
											.nullable(),
									}),
								})
								.default({}),
						})
						.default({})
						.nullable(),
					plateletsDetails: getMultiFormSchema(
						t(`${VALIDATION_TRANSLATION_PREFIX}.platelets.collectionDate`),
						t(`${VALIDATION_TRANSLATION_PREFIX}.platelets.value`),
					),
					hemoglobinDetails: getMultiFormSchema(
						t(`${VALIDATION_TRANSLATION_PREFIX}.hemoglobin.collectionDate`),
						t(`${VALIDATION_TRANSLATION_PREFIX}.hemoglobin.value`),
					),
					...RESULT_QUESTIONS.reduce(
						(acc, { name }) => ({
							...acc,
							[name]: yup
								.string()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.${name}`))
								.nullable(),
							[`${name}Description`]: yup.string().when(name, {
								is: (value: RESULT_TYPES) =>
									[RESULT_TYPES.OTHERS, RESULT_TYPES.DONE].includes(value),
								then: yup
									.string()
									.required(
										t(`${VALIDATION_TRANSLATION_PREFIX}.${name}Description`),
									)
									.nullable(),
							}),
						}),
						{},
					),
					gbs: yup
						.string()
						.required(t(`${VALIDATION_TRANSLATION_PREFIX}.gbs`))
						.nullable(),
					gbsSwapDate: yup
						.date()
						.transform((curr, orig) => (orig === '' ? null : curr))
						.nullable()
						.when('gbs', {
							is: (value: RESULT_TYPES) =>
								[RESULT_TYPES.POSITIVE, RESULT_TYPES.NEGATIVE].includes(value),
							then: yup
								.date()
								.transform((curr, orig) => (orig === '' ? null : curr))
								.nullable()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.gbsSwapDate`)),
						}),
				})
				.default({}),
		})
		.default({});

interface Props {
	onSuccess: () => void;
	patientId: string;
}

const LabResultsForm: React.FC<Props> = ({ onSuccess, patientId }) => {
	const { t } = useTranslation();
	const { data, setData } = useContext(HealthPlanContext);

	const defaultValues = {
		[FieldNames.LAB_RESULTS]: data[FieldNames.LAB_RESULTS] ?? {
			hemoglobinDetails: [{ collectionDate: '', value: '' }],
			plateletsDetails: [{ collectionDate: '', value: '' }],
		},
	};

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(getValidationSchema(t)),
	});

	return (
		<ScrollContainer>
			<div className='col-12'>
				<div className='page-panel mt-1'>
					<FormWrapper formMethods={formMethods}>
						<h3 className={styles.title}>
							<Text tx='base.labResults.title' firstUpper />
						</h3>
						<FormContainer name={FieldNames.LAB_RESULTS}>
							<FormContainer name='bloodGroup'>
								<div className={styles.fieldsWrapper}>
									<SelectField
										isRequired
										name='type'
										label='base.bloodGroup.type'
										shouldTranslateValues={false}
										options={BLOOD_GROUPS.map(group => ({
											value: group,
											label: group,
										}))}>
										<FormCondition controlName='factor.value' equal='negative'>
											<FormContainer name='antiD' settings={{ showLine: true }}>
												<RadioListField
													name={APPLICATION_OF_ANTI_D_LABEL}
													label='base.bloodGroup.antiD.title'
													onFieldChange={value => {
														formMethods.setValue(
															//@ts-ignore
															`${FieldNames.LAB_RESULTS}.bloodGroup.antiD`,
															{
																applicationOfAntiD: value,
																dose: '',
																date: '',
																medicineName: {},
															},
														);
													}}
													data={[
														{
															title: 'base.yes',
															value: 'yes',
														},
														{
															title: 'base.no',
															value: 'no',
														},
													]}
													className={styles.field}
													inLine>
													<FormCondition
														controlName={APPLICATION_OF_ANTI_D_LABEL}
														equal='yes'>
														<FormContainer settings={{ showLine: true }}>
															<DateField
																isRequired
																name='date'
																label='base.bloodGroup.antiD.date'
																className={styles.field}
															/>
															<div className={styles.antiDFieldsWrapper}>
																<PrescriptionMedicamentsSelectField
																	isRequired
																	name='medicineName'
																	label='base.bloodGroup.antiD.medicineName'
																/>
																<InputField
																	isRequired
																	name='dose'
																	label='base.bloodGroup.antiD.dose'
																/>
															</div>
														</FormContainer>
													</FormCondition>
												</RadioListField>
											</FormContainer>
										</FormCondition>
									</SelectField>
									<SelectField
										isRequired
										name='factor'
										label='base.bloodGroup.factor'
										options={[
											{ label: 'base.positive', value: 'positive' },
											{ label: 'base.negative', value: 'negative' },
											{ label: 'base.others', value: 'others' },
										]}
										onFieldChange={() => {
											formMethods.resetField(
												//@ts-ignore
												`${FieldNames.LAB_RESULTS}.bloodGroup.${APPLICATION_OF_ANTI_D_LABEL}`,
											);
										}}>
										<FormCondition controlName='factor.value' equal='others'>
											<FormContainer settings={{ showLine: true }}>
												<InputField
													name='factorOther'
													label='base.bloodGroup.factorSpecify'
													isRequired
												/>
											</FormContainer>
										</FormCondition>
									</SelectField>
								</div>
							</FormContainer>
							<FormMultiplier name='hemoglobinDetails'>
								{({ remove, index, itemsCount }) => (
									<div className={styles.fieldsWrapper}>
										<DateField
											isRequired
											name='collectionDate'
											label='base.hemoglobin.collectionDate'
										/>
										<NumberField
											isRequired
											name='value'
											label='base.hemoglobin.value'
											suffix='g/l'
											className={styles.numberField}
										/>
										{itemsCount > 1 && (
											<div className={styles.removeItemWrapper}>
												{index !== 0 && (
													<RemoveButton
														onClick={() => {
															remove(index);
														}}
													/>
												)}
											</div>
										)}
									</div>
								)}
							</FormMultiplier>
							<FormMultiplier name='plateletsDetails'>
								{({ remove, index, itemsCount }) => (
									<div className={styles.fieldsWrapper}>
										<DateField
											isRequired
											name='collectionDate'
											label='base.platelets.collectionDate'
										/>
										<NumberField
											isRequired
											name='value'
											label='base.platelets.value'
											suffix={
												<p className={styles.suffix}>
													x10
													<sup>9</sup>/l
												</p>
											}
											className={styles.numberField}
										/>
										{itemsCount > 1 && (
											<div className={styles.removeItemWrapper}>
												{index !== 0 && (
													<RemoveButton
														onClick={() => {
															remove(index);
														}}
													/>
												)}
											</div>
										)}
									</div>
								)}
							</FormMultiplier>
							<div
								className={joinClasses([
									styles.fieldsWrapper,
									styles.fieldSeparator,
								])}>
								{RESULT_QUESTIONS.map(({ name, data }) => (
									<RadioListField
										isRequired
										name={name}
										label={`base.${name}`}
										data={data}
										inLine
										className={styles.radioWrapper}>
										<>
											<FormCondition
												controlName={name}
												equal={RESULT_TYPES.OTHERS}>
												<FormContainer settings={{ showLine: true }}>
													<InputField
														isRequired
														name={`${name}Description`}
														label='base.specify'
													/>
												</FormContainer>
											</FormCondition>
											<FormCondition
												controlName={name}
												equal={RESULT_TYPES.DONE}>
												<FormContainer settings={{ showLine: true }}>
													<InputField
														isRequired
														name={`${name}Description`}
														label='base.additionalInformation'
													/>
												</FormContainer>
											</FormCondition>
										</>
									</RadioListField>
								))}
							</div>
							<RadioListField
								isRequired
								name='gbs'
								label='base.gbs'
								data={[
									{
										title: 'base.positive',
										value: RESULT_TYPES.POSITIVE,
									},
									{
										title: 'base.negative',
										value: RESULT_TYPES.NEGATIVE,
									},
									{
										title: 'base.notCollected',
										value: RESULT_TYPES.NOT_COLLECTED,
									},
								]}
								inLine
								className={styles.radioWrapper}>
								<FormCondition controlName='gbs' equal={RESULT_TYPES.POSITIVE}>
									<FormContainer settings={{ showLine: true }}>
										<DateField
											isRequired
											name='gbsSwapDate'
											label='base.gbsSwapDate'
										/>
									</FormContainer>
								</FormCondition>
								<FormCondition controlName='gbs' equal={RESULT_TYPES.NEGATIVE}>
									<FormContainer settings={{ showLine: true }}>
										<DateField
											isRequired
											name='gbsSwapDate'
											label='base.gbsSwapDate'
										/>
									</FormContainer>
								</FormCondition>
							</RadioListField>
							<div className='d-flex mt-4 justify-content-between'>
								<SubmitButton
									title={'base.shared.continue'}
									onSubmit={fields => {
										setData(fields);
										onSuccess();
									}}
								/>
							</div>
						</FormContainer>
					</FormWrapper>
				</div>
			</div>
		</ScrollContainer>
	);
};

export default LabResultsForm;
